import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, getActiveAccountHeader } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Lock, Crown, Check, Star, Clock } from 'lucide-react';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// Reusable component to show when trying to access locked features
export const SubscriptionLockDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Lock className="w-5 h-5 text-amber-500" />
            Subscription Required
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            Your free trial has ended. Subscribe to continue managing your horses with full access.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Quick plan comparison */}
          <div className="grid gap-3">
            <div className="p-3 rounded-lg border bg-muted/30">
              <div className="flex justify-between items-center">
                <span className="font-semibold">EquiVault Rider</span>
                <span className="text-saddle font-bold">$8.99/mo</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">Up to 5 horses, invite 1 helper</p>
            </div>
            <div className="p-3 rounded-lg border-2 border-forest bg-forest/5">
              <div className="flex justify-between items-center">
                <span className="font-semibold flex items-center gap-1">
                  <Star className="w-4 h-4 text-forest" />
                  EquiVault Pro
                </span>
                <span className="text-forest font-bold">$14.99/mo</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">Unlimited horses, invite your whole team</p>
            </div>
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Maybe Later
            </Button>
            <Button 
              className="flex-1 bg-forest hover:bg-forest-dark gap-2"
              onClick={() => {
                onOpenChange(false);
                navigate('/account');
              }}
            >
              <Crown className="w-4 h-4" />
              Subscribe Now
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Dialog for upgrade prompts (when hitting plan limits)
export const UpgradePromptDialog = ({ open, onOpenChange, type = 'horses', currentCount = 0, limit = 5 }) => {
  const navigate = useNavigate();
  
  const messages = {
    horses: {
      title: "You've Got a Growing Stable!",
      description: `You have ${currentCount} horses and your current plan supports up to ${limit}. Upgrade to EquiVault Pro for unlimited horses.`,
      icon: "🐴"
    },
    team: {
      title: "Your Team is Growing!",
      description: `You've reached your team member limit (${limit}). Upgrade to EquiVault Pro to invite unlimited team members.`,
      icon: "👥"
    }
  };
  
  const msg = messages[type] || messages.horses;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <span className="text-2xl">{msg.icon}</span>
            {msg.title}
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            {msg.description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="p-4 rounded-lg border-2 border-forest bg-forest/5">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold flex items-center gap-1">
                <Star className="w-4 h-4 text-forest" />
                EquiVault Pro
              </span>
              <span className="text-forest font-bold">$14.99/mo</span>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-forest" />
                Unlimited horses
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-forest" />
                Unlimited team members
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-forest" />
                Stable Dashboard
              </li>
            </ul>
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Maybe Later
            </Button>
            <Button 
              className="flex-1 bg-forest hover:bg-forest-dark gap-2"
              onClick={() => {
                onOpenChange(false);
                navigate('/account');
              }}
            >
              <Crown className="w-4 h-4" />
              Upgrade to Pro
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Visual banner for read-only mode OR trial expiring soon
export const ReadOnlyBanner = () => {
  const navigate = useNavigate();
  const { isExpired, isTrialActive, subscription } = useSubscription();
  
  // Show warning banner if trial is expiring soon (7 days or less)
  if (isTrialActive() && subscription?.trial_days_remaining <= 7) {
    const daysLeft = subscription.trial_days_remaining;
    return (
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-amber-800">
          <Clock className="w-4 h-4" />
          <span className="text-sm font-medium">
            Trial ending soon — {daysLeft} {daysLeft === 1 ? 'day' : 'days'} remaining
          </span>
        </div>
        <Button 
          size="sm" 
          className="bg-forest hover:bg-forest-dark text-white h-7 text-xs"
          onClick={() => navigate('/account')}
        >
          Subscribe Now
        </Button>
      </div>
    );
  }
  
  // Show read-only banner for expired users
  if (!isExpired()) return null;
  
  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-2 text-amber-800">
        <Lock className="w-4 h-4" />
        <span className="text-sm font-medium">Read-only mode — Your trial has ended</span>
      </div>
      <Button 
        size="sm" 
        className="bg-amber-600 hover:bg-amber-700 text-white h-7 text-xs"
        onClick={() => navigate('/account')}
      >
        Subscribe to Edit
      </Button>
    </div>
  );
};

export const SubscriptionProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLockDialog, setShowLockDialog] = useState(false);

  const fetchSubscription = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // Get fresh headers each time
      const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      if (activeAccountId) {
        headers['X-Active-Account'] = activeAccountId;
      }
      
      console.log('Fetching subscription with headers:', headers);

      const response = await fetch(`${API}/payments/subscription`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Subscription data received:', data);
        setSubscription(data);
      } else {
        console.error('Subscription fetch failed:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, [token, user?.subscription_plan]);

  // Re-fetch subscription when active account changes (for account switcher)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'activeAccountId') {
        fetchSubscription();
      }
    };
    
    // Also check on focus in case storage changed in another tab
    const handleFocus = () => {
      fetchSubscription();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [token]);

  // Helper functions for feature gating
  const canAddEdit = () => {
    if (!subscription) return false;
    return subscription.can_add_edit === true;
  };

  const canAddHorse = (currentHorseCount) => {
    if (!subscription) return false;
    if (!subscription.can_add_edit) return false;
    if (subscription.horse_limit === -1) return true; // Unlimited
    return currentHorseCount < subscription.horse_limit;
  };

  const hasStableDashboard = () => {
    if (!subscription) return false;
    return subscription.has_stable_dashboard === true;
  };

  const isPro = () => {
    return subscription?.plan === 'pro';
  };

  const isRider = () => {
    return subscription?.plan === 'rider';
  };

  const isTrialActive = () => {
    return subscription?.is_trial_active === true;
  };

  const isExpired = () => {
    if (!subscription) return true;
    return !subscription.can_add_edit && subscription.plan !== 'rider' && subscription.plan !== 'pro';
  };

  const getHorseLimit = () => {
    return subscription?.horse_limit ?? 0;
  };

  const refresh = () => {
    fetchSubscription();
  };

  // Function to check permission and show dialog if locked
  const requireSubscription = (callback) => {
    if (canAddEdit()) {
      if (callback) callback();
      return true;
    } else {
      setShowLockDialog(true);
      return false;
    }
  };

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        loading,
        canAddEdit,
        canAddHorse,
        hasStableDashboard,
        isPro,
        isRider,
        isTrialActive,
        isExpired,
        getHorseLimit,
        refresh,
        requireSubscription,
        showLockDialog,
        setShowLockDialog
      }}
    >
      {children}
      <SubscriptionLockDialog 
        open={showLockDialog} 
        onOpenChange={setShowLockDialog} 
      />
    </SubscriptionContext.Provider>
  );
};
